/**
 * The EMT helper (flag `emt_assistant`, Part A6): a small button in the corner of every EMT page that
 * opens a panel. Rules and security: src/lib/assistant/emtAssistant.ts.
 *
 *  - Proactive tips come from the EMT's own data on this device (no AI call): "Profile complete!" with
 *    confetti the first time; on a job with something missing, "For [Company] [Job] you're missing
 *    EVOC. Want info on how to get it?" (Yes goes to Find training for it and leaves a "Back to what
 *    you were doing" link that returns to the exact page). "How to get it" is offered only for items
 *    NOT on the profile; a credential on the profile whose document is not reviewed yet gets "your BLS
 *    document has not been reviewed yet" with a link to the Account page's Credentials section. Then
 *    the single best next step for the score.
 *  - Questions go to the emtAssistantAsk server function (30 a day).
 *  - Minimize closes the panel; "Not now" also stops tips from popping up for this session.
 *  - Keyboard: the button and panel are real buttons/dialog, Escape closes and returns focus. Status
 *    and tips are announced politely. Works at 390 wide (the panel fits the screen width).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Lightbulb, Loader2, PartyPopper, Send, Sparkles, X } from 'lucide-react';
import { useAuthCtx } from '../../context/AuthContext';
import { useFeatureFlag } from '../../context/FeatureFlagsContext';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { findTrainingHref } from '../../lib/institutes';
import {
  ASSISTANT_MAX_QUESTION_CHARS,
  EMT_ASSISTANT_FLAG,
  PROFILE_COMPLETE_TIP,
  jobMissingTip,
  jobPendingTip,
  jobLowerScoreTip,
  type AssistantLink,
  type ProactiveTip,
} from '../../lib/assistant/emtAssistant';
import {
  askAssistant,
  clearReturnPoint,
  explainAssistantError,
  getAssistantJobContext,
  readReturnPoint,
  saveReturnPoint,
  subscribeAssistantJobContext,
  type ReturnPoint,
} from '../../lib/assistant/assistantClient';
import { useEmtReadinessView } from '../readiness/ReadinessV2';
import { useCompetitiveness } from '../readiness/useJobReadiness';
import Confetti from '../ui/Confetti';
import { countLabel } from '../../lib/text/plural';

// Solid blue-700 in both themes: white text on it is 6.7:1 (the dark theme's --rr-info is too light for white text).
const BTN_BG = '#1D4ED8';
const SNOOZE_KEY = 'rr-assistant-not-now';
const SEEN_KEY = 'rr-assistant-seen-tips';
const completeKey = (uid: string) => `rr-assistant-complete:${uid}`;

function session(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function setSession(key: string, v: string) {
  try {
    window.sessionStorage.setItem(key, v);
  } catch {
    /* storage blocked */
  }
}
function local(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function setLocal(key: string, v: string | null) {
  try {
    if (v == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, v);
  } catch {
    /* storage blocked */
  }
}

type Turn = { id: number; role: 'user' | 'assistant'; text: string; links?: AssistantLink[] };

export default function EmtAssistant() {
  const enabled = useFeatureFlag(EMT_ASSISTANT_FLAG);
  const { user, profile } = useAuthCtx();
  if (!enabled || !user || (profile as { role?: string } | null)?.role !== 'emt') return null;
  return <AssistantInner uid={user.uid} profile={profile as unknown as Record<string, unknown>} />;
}

function AssistantInner({ uid, profile }: { uid: string; profile: Record<string, unknown> }) {
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = usePrefersReducedMotion();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [notNow, setNotNow] = useState(() => session(SNOOZE_KEY) === '1');
  const [seen, setSeen] = useState<string[]>(() => {
    try {
      return JSON.parse(session(SEEN_KEY) || '[]') as string[];
    } catch {
      return [];
    }
  });
  const [confetti, setConfetti] = useState(false);
  const [returnPoint, setReturnPoint] = useState<ReturnPoint | null>(() => readReturnPoint());
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [left, setLeft] = useState<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const turnId = useRef(0);

  const jobCtx = useSyncExternalStore(subscribeAssistantJobContext, getAssistantJobContext, () => null);
  const view = useEmtReadinessView(profile, []);
  const competitiveness = useCompetitiveness(profile, open);
  const here = `${location.pathname}${location.search}${location.hash}`;

  // Profile complete: the tip and confetti the FIRST time (per browser); reset when no longer complete.
  const complete = view?.complete === true;
  const [completeNew, setCompleteNew] = useState(false);
  useEffect(() => {
    if (!view) return;
    const was = local(completeKey(uid)) === '1';
    if (complete && !was) {
      setLocal(completeKey(uid), '1');
      setCompleteNew(true);
      if (!reducedMotion) setConfetti(true);
    } else if (!complete && was) {
      setLocal(completeKey(uid), null);
    }
  }, [complete, uid, view, reducedMotion]);

  const tips = useMemo<ProactiveTip[]>(() => {
    const list: ProactiveTip[] = [];
    // On a job page, what is missing for THIS job comes first.
    if (jobCtx && location.pathname === `/jobs/${jobCtx.jobId}`) {
      const t = jobMissingTip(jobCtx.company, jobCtx.title, jobCtx.missing, (c) => findTrainingHref(c), jobCtx.jobId);
      if (t) list.push(t);
      const p = jobPendingTip(jobCtx.company, jobCtx.title, jobCtx.pending, jobCtx.jobId);
      if (p) list.push(p);
      const l = jobLowerScoreTip(jobCtx.company, jobCtx.title, jobCtx.lower?.text, jobCtx.lower?.href, jobCtx.jobId);
      if (l) list.push(l);
    }
    if (complete && completeNew) list.push(PROFILE_COMPLETE_TIP);
    const c = competitiveness.state;
    const top = c.status === 'ready' && c.view.enough ? c.view.changes[0] : null;
    if (top) list.push({ kind: 'next_step', id: `next:${top.id}`, text: `Best next step: ${top.detail}`, href: top.href });
    else if (view?.step) list.push({ kind: 'next_step', id: `next:${view.step.id}`, text: `Best next step: ${view.step.label}. ${view.step.description}`, href: view.step.href });
    return list;
  }, [complete, completeNew, jobCtx, location.pathname, competitiveness.state, view?.step]);

  // On a job page, wait (up to 4 seconds) for the page to say what is missing before any tip pops up,
  // so the job's own tip is the first thing shown there.
  const onJobPage = /^\/jobs\/[^/]+$/.test(location.pathname);
  const [jobWaitOver, setJobWaitOver] = useState(false);
  useEffect(() => {
    setJobWaitOver(false);
    const t = window.setTimeout(() => setJobWaitOver(true), 4000);
    return () => window.clearTimeout(t);
  }, [location.pathname]);
  const pageSettled = !onJobPage || jobWaitOver || (jobCtx != null && location.pathname === `/jobs/${jobCtx.jobId}`);
  // A tip pops up beside the button once per session (job tips and "complete" only), unless "Not now".
  const peek = !open && !notNow && pageSettled ? tips.find((t) => t.kind !== 'next_step' && !seen.includes(t.id)) : undefined;
  const markSeen = useCallback((id: string) => {
    setSeen((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id].slice(-100);
      setSession(SEEN_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // Returning to the saved page clears the back link.
  useEffect(() => {
    if (returnPoint && returnPoint.href === here) {
      clearReturnPoint();
      setReturnPoint(null);
    }
  }, [here, returnPoint]);

  const openPanel = () => {
    setOpen(true);
    if (peek) markSeen(peek.id);
  };
  const closePanel = useCallback(() => {
    setOpen(false);
    window.setTimeout(() => buttonRef.current?.focus(), 0);
  }, []);
  const chooseNotNow = () => {
    setSession(SNOOZE_KEY, '1');
    setNotNow(true);
    tips.forEach((t) => markSeen(t.id));
    closePanel();
  };

  const goGetIt = (tip: Extract<ProactiveTip, { kind: 'job_missing' }>) => {
    const jobLabel = jobCtx
      ? jobCtx.title && jobCtx.company
        ? `${jobCtx.title} at ${jobCtx.company}`
        : jobCtx.title || jobCtx.company || 'the job you were on'
      : '';
    const p = { href: here, label: jobLabel || 'the page you were on' };
    saveReturnPoint(p);
    setReturnPoint(p);
    markSeen(tip.id);
    setOpen(false);
    navigate(tip.yesHref);
  };

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || asking) return;
    setError(null);
    setAsking(true);
    setTurns((t) => [...t, { id: ++turnId.current, role: 'user', text: q }]);
    setQuestion('');
    try {
      const onJob = jobCtx && location.pathname === `/jobs/${jobCtx.jobId}` ? jobCtx.jobId : null;
      const r = await askAssistant(q, onJob);
      if (r.available) {
        setTurns((t) => [...t, { id: ++turnId.current, role: 'assistant', text: r.answer, links: r.links }]);
        setLeft(r.questionsLeftToday);
      } else {
        setError(r.reason === 'flag_off' ? 'The helper is switched off right now.' : "I couldn't answer that just now. Please try again in a moment.");
      }
    } catch (err) {
      setError(explainAssistantError(err) ?? "I couldn't reach the helper. Check your connection and try again.");
    } finally {
      setAsking(false);
    }
  };

  const showBack = returnPoint && returnPoint.href !== here;
  // On a job page the phone apply bar is at the bottom; sit above it there.
  // Above the phone bar on pages that have one (job page: below lg; interview request page: below sm).
  const lift = location.pathname.startsWith('/jobs/') ? 'bottom-24 lg:bottom-6' : location.pathname.startsWith('/applications/interview-request/') ? 'bottom-28 sm:bottom-6' : 'bottom-6';

  return (
    <>
      {confetti && <Confetti onDone={() => setConfetti(false)} />}
      <div className={`fixed right-4 z-[45] flex flex-col items-end gap-2 sm:right-6 ${lift}`} data-testid="emt-assistant">
        {showBack && !open && (
          <Link
            to={returnPoint!.href}
            onClick={() => {
              clearReturnPoint();
              setReturnPoint(null);
            }}
            className="inline-flex min-h-10 max-w-[min(20rem,calc(100vw-2rem))] items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] shadow-md hover:bg-[var(--surface-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rr-info)]"
            data-testid="assistant-back-link"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">Back to what you were doing</span>
          </Link>
        )}
        {peek && (
          <div
            role="status"
            className="w-[min(20rem,calc(100vw-2rem))] rounded-2xl border border-[var(--border-default)] bg-[var(--surface-card)] p-3 text-sm text-[var(--text-primary)] shadow-lg"
            data-testid="assistant-peek"
            data-tip={peek.kind}
          >
            <div className="flex items-start gap-2">
              {peek.kind === 'complete' ? <PartyPopper className="mt-0.5 h-4 w-4 shrink-0 text-[var(--rr-success-fg,var(--rr-success))]" aria-hidden /> : <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-[var(--rr-info)]" aria-hidden />}
              <p className="min-w-0 flex-1">{peek.text}</p>
              <button type="button" onClick={() => markSeen(peek.id)} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]" aria-label="Dismiss tip">
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {peek.kind === 'job_pending' && (
              <div className="mt-2 flex gap-2">
                <Link
                  to={peek.href}
                  onClick={() => markSeen(peek.id)}
                  className="inline-flex min-h-9 items-center rounded-lg px-3 text-sm font-semibold text-white"
                  style={{ background: BTN_BG }}
                  data-testid="assistant-peek-pending"
                >
                  Open Credentials
                </Link>
                <button type="button" onClick={() => markSeen(peek.id)} className="inline-flex min-h-9 items-center rounded-lg border border-[var(--border-default)] px-3 text-sm font-semibold">
                  No thanks
                </button>
              </div>
            )}
            {peek.kind === 'job_missing' && (
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => goGetIt(peek)} className="inline-flex min-h-9 items-center rounded-lg px-3 text-sm font-semibold text-white" style={{ background: BTN_BG }} data-testid="assistant-peek-yes">
                  Yes, show me
                </button>
                <button type="button" onClick={() => markSeen(peek.id)} className="inline-flex min-h-9 items-center rounded-lg border border-[var(--border-default)] px-3 text-sm font-semibold">
                  No thanks
                </button>
              </div>
            )}
          </div>
        )}
        {open && (
          <div
            id={panelId}
            role="dialog"
            aria-modal="false"
            aria-label="Arevina helper"
            className="flex max-h-[min(36rem,calc(100vh-8rem))] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-[var(--border-default)] bg-[var(--surface-card)] shadow-2xl"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                closePanel();
              }
            }}
            data-testid="assistant-panel"
          >
            <div className="flex items-center gap-2 border-b border-[var(--border-subtle)] px-4 py-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full" style={{ background: 'var(--rr-info-light)', color: 'var(--rr-info)' }} aria-hidden>
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-[var(--text-primary)]">Arevina helper</p>
                <p className="text-xs text-[var(--text-muted)]">Tips from your profile, and quick answers.</p>
              </div>
              <button type="button" onClick={chooseNotNow} className="min-h-9 rounded-lg px-2 text-xs font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]" data-testid="assistant-not-now">
                Not now
              </button>
              <button type="button" onClick={closePanel} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-hover)]" aria-label="Minimize helper" data-testid="assistant-minimize">
                <ChevronDown className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3" aria-live="polite">
              {showBack && (
                <Link
                  to={returnPoint!.href}
                  onClick={() => {
                    clearReturnPoint();
                    setReturnPoint(null);
                  }}
                  className="flex items-center gap-1.5 rounded-xl border border-[var(--border-default)] px-3 py-2 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--surface-hover)]"
                  data-testid="assistant-panel-back"
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Back to {returnPoint!.label}
                </Link>
              )}
              {tips.map((t) => (
                <div key={t.id} className="rounded-xl p-3 text-sm" style={{ background: t.kind === 'complete' ? 'var(--rr-success-light)' : 'var(--rr-info-light)' }} data-testid={`assistant-tip-${t.kind}`}>
                  <p className="text-[var(--text-primary)]">{t.text}</p>
                  {t.kind === 'job_missing' && (
                    <button type="button" onClick={() => goGetIt(t)} className="mt-2 inline-flex min-h-9 items-center rounded-lg px-3 text-sm font-semibold text-white" style={{ background: BTN_BG }} data-testid="assistant-tip-yes">
                      Yes, show me
                    </button>
                  )}
                  {t.kind === 'job_pending' && (
                    <Link
                      to={t.href}
                      onClick={() => {
                        markSeen(t.id);
                        setOpen(false);
                      }}
                      className="mt-2 inline-flex min-h-9 items-center rounded-lg px-3 text-sm font-semibold text-white"
                      style={{ background: BTN_BG }}
                      data-testid="assistant-tip-pending"
                    >
                      Open Credentials
                    </Link>
                  )}
                  {t.kind === 'next_step' && (
                    <Link to={t.href} onClick={() => setOpen(false)} className="mt-2 inline-flex min-h-9 items-center rounded-lg border border-[var(--border-default)] bg-[var(--surface-card)] px-3 text-sm font-semibold text-[var(--text-primary)]">
                      Do it
                    </Link>
                  )}
                </div>
              ))}
              {turns.length === 0 && (
                <p className="text-sm text-[var(--text-secondary)]">
                  Ask me things like &quot;what do I need for this job?&quot;, &quot;how do I raise my score?&quot; or &quot;where do I upload my BLS?&quot;
                </p>
              )}
              {turns.map((t) => (
                <div key={t.id} className={t.role === 'user' ? 'ml-8 rounded-xl bg-[var(--surface-muted)] p-3 text-sm text-[var(--text-primary)]' : 'mr-4 text-sm text-[var(--text-primary)]'} data-testid={`assistant-turn-${t.role}`}>
                  <p className="whitespace-pre-wrap">{t.text}</p>
                  {t.links && t.links.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {t.links.map((l) =>
                        l.href.startsWith('/') ? (
                          <li key={l.href}>
                            <Link to={l.href} className="inline-flex min-h-9 items-center rounded-lg border border-[var(--border-default)] px-3 text-sm font-semibold text-[var(--rr-info)]" data-testid="assistant-link">
                              {l.label}
                            </Link>
                          </li>
                        ) : (
                          <li key={l.href}>
                            <a href={l.href} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-9 items-center rounded-lg border border-[var(--border-default)] px-3 text-sm font-semibold text-[var(--rr-info)]" data-testid="assistant-link">
                              {l.label} (opens a new tab)
                            </a>
                          </li>
                        ),
                      )}
                    </ul>
                  )}
                </div>
              ))}
              {asking && (
                <p className="inline-flex items-center gap-2 text-sm text-[var(--text-muted)]">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Thinking…
                </p>
              )}
              {error && (
                <p className="text-sm text-[var(--rr-error-fg,var(--rr-error))]" role="alert" data-testid="assistant-error">
                  {error}
                </p>
              )}
            </div>
            <form onSubmit={submit} className="border-t border-[var(--border-subtle)] p-3">
              <label htmlFor={`${panelId}-q`} className="sr-only">
                Your question
              </label>
              <div className="flex items-end gap-2">
                <textarea
                  id={`${panelId}-q`}
                  ref={inputRef}
                  value={question}
                  maxLength={ASSISTANT_MAX_QUESTION_CHARS}
                  rows={2}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void submit(e as unknown as FormEvent);
                    }
                  }}
                  placeholder="Ask a question"
                  className="min-h-11 flex-1 resize-none rounded-xl border border-[var(--border-default)] bg-[var(--surface-page)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rr-info)]"
                  data-testid="assistant-input"
                />
                <button type="submit" disabled={asking || !question.trim()} className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-white disabled:opacity-50" style={{ background: BTN_BG }} aria-label="Send question" data-testid="assistant-send">
                  <Send className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <p className="mt-1 text-xs text-[var(--text-muted)]">
                I can be wrong, and I don't give medical or legal advice.{left != null ? ` ${countLabel(left, 'question')} left today.` : ''}
              </p>
            </form>
          </div>
        )}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => (open ? closePanel() : openPanel())}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={open ? 'Minimize Arevina helper' : `Open Arevina helper${tips.length ? `, ${tips.length} tip${tips.length === 1 ? '' : 's'}` : ''}`}
          className="relative inline-flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--rr-info)] focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:scale-100"
          style={{ background: BTN_BG }}
          data-testid="assistant-button"
        >
          <Sparkles className="h-6 w-6" aria-hidden />
          {!open && tips.some((t) => !seen.includes(t.id)) && (
            <span className="absolute right-0.5 top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white" style={{ background: 'var(--rr-red)' }} aria-hidden />
          )}
        </button>
      </div>
    </>
  );
}
